// src/app/modules/Auth/pages/ResetPassword.jsx
import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { injectIntl } from "react-intl";
import { reset_password } from "../_redux/authCrud";
import './auth.scss';
import { Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const initialValues = {
  password: "",
  confirm_password: "",
};

function ResetPassword(props) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
    
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

//   const email = props.location.state?.email || '';

  const { email } = location.state || {};

  useEffect(() => {
    if (!email) {
      navigate('/auth/forgot-password');
    }
  }, [email, props.history]);

  const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain a lowercase letter")
      .matches(/[A-Z]/, "Password must contain an uppercase letter")
      .matches(/[0-9]/, "Password must contain a number")
      .required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: ResetPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      const fullCode = code.join('');

      if (fullCode.length !== 6) {
        setStatus('Please enter the complete 6-digit code');
        return;
      }

      setLoading(true);
      setStatus('');

      const payload = {
        email: email,
        code: fullCode,
        password: values.password,
      };

      reset_password(payload)
        .then((response) => {
          setLoading(false);
          setSubmitting(false);

          setStatus('Password reset successful! Redirecting to login...');
          setTimeout(() => {
            navigate('/auth/login');
          }, 1000);
        
        })
        .catch((error) => {
          setLoading(false);
          setSubmitting(false);
          const message = error.response?.data?.message || 'An error occurred';
          setStatus(message);
        });
    },
  });

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = pastedData.split('');
    while (newCode.length < 6) newCode.push('');
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex(c => !c);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  return (
    <div className="clean-login-card">
      <div className="login-header">
        <div className="verification-icon">
          <Lock size={32} />
        </div>
        <h1 className="login-title">Reset Password</h1>
        <p className="login-subtitle">
          Enter the code sent to <strong>{email}</strong><br />
          and choose a new password
        </p>
      </div>

      {formik.status && (
        <div className={`login-alert ${formik.status.includes('successful') ? 'success' : ''}`}>
          {formik.status}
        </div>
      )}

      <form onSubmit={formik.handleSubmit} className="login-form">
        {/* Verification Code */}
        <div className="form-group">
          <label className="form-label">Verification Code</label>
          <div className="pin-inputs" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="pin-input"
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        {/* New Password */}
        <div className="form-group">
          <label className="form-label">New Password</label>
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter new password"
              className={`form-control ${
                formik.touched.password && formik.errors.password ? 'error' : ''
              }`}
              {...formik.getFieldProps("password")}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className="form-error">{formik.errors.password}</div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="form-group">
          <label className="form-label">Confirm New Password</label>
          <div className="password-field">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirm_password"
              placeholder="Confirm new password"
              className={`form-control ${
                formik.touched.confirm_password && formik.errors.confirm_password ? 'error' : ''
              }`}
              {...formik.getFieldProps("confirm_password")}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {formik.touched.confirm_password && formik.errors.confirm_password && (
            <div className="form-error">{formik.errors.confirm_password}</div>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={formik.isSubmitting || loading || code.some(c => !c)}
          className="submit-btn"
        >
          {loading ? (
            <>
              Resetting password...
              <span className="spinner"></span>
            </>
          ) : (
            'Reset Password'
          )}
        </button>

        {/* Back to Login */}
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/auth/login')}
        >
          <ArrowLeft size={16} />
          Back to Sign In
        </button>
      </form>
    </div>
  );
}

export default injectIntl(ResetPassword);